import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import AuthLayout from './layouts/AuthLayout';
import AdminLayout from './layouts/AdminLayout';
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import UsersPage from './pages/admin/users/UsersPage';
import UserFormPage from './pages/admin/users/UserFormPage';
import UserDetailPage from './pages/admin/users/UserDetailPage';
import RolesPage from './pages/admin/roles/RolesPage';
import PlansPage from './pages/admin/plans/PlansPage';
import PlanFormPage from './pages/admin/plans/PlanFormPage';
import CmsListPage from './pages/cms/CmsListPage';
import CmsFormPage from './pages/cms/CmsFormPage';
import FaqsPage from './pages/admin/faqs/FaqsPage';
import IntegrationsPage from './pages/admin/settings/IntegrationsPage';
import SiteSettingsPage from './pages/admin/settings/SiteSettingsPage';
import AdminSupportPage from './pages/admin/support/AdminSupportPage';
import AnalyticsPage from './pages/admin/analytics/AnalyticsPage';
import ContactInquiriesPage from './pages/admin/contact-inquiries/ContactInquiriesPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/users" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Auth routes — no sidebar */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <ForgotPasswordPage />
            </GuestRoute>
          }
        />
      </Route>

      {/* Admin routes — with sidebar */}
      <Route
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/create" element={<UserFormPage />} />
        <Route path="/users/:id" element={<UserDetailPage />} />
        <Route path="/users/:id/edit" element={<UserFormPage />} />
        <Route path="/roles" element={<RolesPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/plans/new" element={<PlanFormPage />} />
        <Route path="/plans/:id/edit" element={<PlanFormPage />} />
        <Route path="/cms" element={<CmsListPage />} />
        <Route path="/cms/new" element={<CmsFormPage />} />
        <Route path="/cms/:id/edit" element={<CmsFormPage />} />
        <Route path="/faqs" element={<FaqsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/settings" element={<SiteSettingsPage />} />
        <Route path="/support" element={<AdminSupportPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/contact-inquiries" element={<ContactInquiriesPage />} />
        <Route path="/" element={<Navigate to="/users" replace />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
